<!-- Header -->
<div class="chat-header">
  <h3 class="chat-title">{{ chatTitle }}</h3>
  <div class="header-actions">
    <button
      nz-button
      nzType="default"
      nzSize="small"
      class="artifacts-button"
      type="button"
      nz-popover
      nzPopoverTrigger="click"
      nzPopoverPlacement="bottomRight"
      [nzPopoverContent]="artifactsPopoverTemplate"
      [nzPopoverOverlayClassName]="'chat-artifacts-popover'"
      [(nzPopoverVisible)]="isArtifactsPanelOpen"
    >
      <span class="artifacts-button-content">
        <svg-icon src="assets/icons/files-content.svg" svgClass="badge-files-content-icon" />
        <span>Artifacts</span>
      </span>
      <span class="artifacts-count-pill">{{ getTotalArtifactCount() }}</span>
    </button>
    <gains-ellipsis-menu
      *ngIf="!readOnly"
      class="card-menu"
      [menuItems]="currentChatMenuItems"
      [dropdownWidth]="'200px'"
      (changeEvent)="onEllipsisMenuAction($event)"
    ></gains-ellipsis-menu>
  </div>
</div>

<ng-template #artifactsPopoverTemplate>
  <div class="artifacts-popover-content">
    <div class="artifacts-dropdown-list" *ngIf="chatArtifacts.length > 0; else noArtifactsTemplate">
      <button type="button" class="artifact-list-card" *ngFor="let artifact of chatArtifacts; trackBy: trackByArtifactId" (click)="onArtifactCardClick(artifact)">
        <div class="artifact-list-top-row">
          <div class="artifact-list-title">{{ artifact.title }}</div>
          <span class="artifact-list-type">
            <svg-icon src="assets/icons/code.svg" class="artifact-type-icon"></svg-icon>
            {{ getArtifactTypeLabel(artifact.type) }}
          </span>
        </div>
        <div class="artifact-list-meta">
          <span class="artifact-generated-by">Generated by DEO Assist</span>
        </div>
      </button>
    </div>

    <ng-template #noArtifactsTemplate>
      <div class="artifacts-empty-state">No artifacts available for this chat.</div>
    </ng-template>
  </div>
</ng-template>

<!-- Messages -->
<div class="messages-container" #messagesContainer>
  <div
    class="message"
    *ngFor="let message of messages; trackBy: trackByMessageId"
    [class.user-message]="message.role === 'user'"
    [class.assistant-message]="message.role === 'assistant'"
  >
    <div class="message-avatar">
      <nz-avatar
        [nzSrc]="message.role === 'user' ? 'assets/icons/user-white.svg' : 'assets/icons/deo-agent-icon.svg'"
        [ngClass]="message.role === 'user' ? 'chat-message-avatar user-avatar' : 'chat-message-avatar agent-avatar'"
      >
      </nz-avatar>
    </div>
    <div class="message-body">
      <div class="message-header">
        <span class="sender-name">
          {{ messageAuthor(message) }}
        </span>
        <span class="message-time">
          {{ formatTime(message.timestamp) }}
        </span>
      </div>
      <div class="message-content">
        <!-- The agent's notes and lookups, reported before the answer (DATA-1629) -->
        <deo-message-progress
          *ngIf="message.progressSteps?.length"
          [steps]="message.progressSteps!"
          [collapsed]="isProgressCollapsed(message)"
          (toggled$)="toggleProgress(message)"
        ></deo-message-progress>

        <!-- Typing dots: shown only during heartbeat phase (streaming active, no content yet) -->
        <div class="typing-indicator" *ngIf="message.isTyping && !message.content">
          <span class="typing-text">DEO is thinking</span>
          <div class="typing-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>

        <!-- Message content: shown once content is available (streaming or complete) -->
        <div class="message-text" *ngIf="message.content">
          <!-- The caret would keep blinking at the end of a finished answer during the artifact phase; the activity line reports that instead. -->
          <div [class.streaming-cursor]="message.isStreaming && !message.activity" [innerHTML]="formatMessageContent(getDisplayedContent(message))"></div>

          <div class="message-attachment-chip" *ngIf="message.attachment && !message.isTyping">
            <svg-icon [src]="getFileIcon(message.attachment.name)" class="file-type-icon"></svg-icon>
            <span class="file-name">{{ message.attachment.name }}</span>
            <span class="file-size">{{ formatFileSize(message.attachment.size) }}</span>
          </div>

          <div class="message-artifacts" *ngIf="message.artifacts && message.artifacts.length > 0">
            <div class="artifacts-header">
              <svg-icon src="assets/icons/files-content.svg" svgClass="files-content-icon" />
              <span>{{ message.artifacts.length }} Artifact(s) Created</span>
            </div>
            <div class="artifacts-grid">
              <div class="artifact-card" tabindex="0" *ngFor="let artifact of message.artifacts" (click)="onViewArtifact(artifact)" (keydown.enter)="onViewArtifact(artifact)">
                <div class="artifact-card-header">
                  <svg-icon [src]="getArtifactIcon(artifact.type)" [svgStyle]="{ color: getArtifactTypeColor(artifact.type) }" [svgClass]="'artifact-icon-size'" />
                  <span class="artifact-type-badge">{{ artifact.type }}</span>
                </div>
                <div class="artifact-card-title">{{ artifact.title }}</div>

                <div class="artifact-card-action">
                  <button nz-button nzType="primary" nzSize="small">View</button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Shown while the backend reports work after the answer text, e.g. building the visualization -->
        <div class="activity-indicator" *ngIf="message.activity" role="status" aria-live="polite">
          <span class="activity-text">{{ getActivityLabel(message) }}</span>
          <div class="typing-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>

        <deo-conversation-chips
          *ngIf="isLastMessage(message) && clarificationOptions.length"
          [prompts]="clarificationOptions"
          [disabled]="isResponseInProgress"
          label="Suggested replies"
          (chosen$)="onClarificationChosen($event)"
        ></deo-conversation-chips>

        <deo-ai-assist-feedback
          *ngIf="isFeedbackEnabled"
          [isTyping]="!!message.isTyping"
          [isAssistant]="message.role === 'assistant'"
          [isPositiveFeedback]="message.isPositiveFeedback"
          [isBusy]="isFeedbackBusy(message)"
          (like)="likeMessage(message)"
          (dislike)="dislikeMessage(message)"
          (feedbackCleared$)="clearFeedback(message)"
          (copyClicked)="copyMessage(message)"
          (likeFeedbackSubmit)="onLikeFeedbackSubmit(message, $event)"
          (dislikeFeedbackSubmit)="onDislikeFeedbackSubmit(message, $event)"
        ></deo-ai-assist-feedback>
      </div>
    </div>
  </div>
</div>

<!-- Input Area -->
<div class="input-container">
  <p class="read-only-notice" *ngIf="readOnly" role="status">This chat is shared with you read-only.</p>
  <div class="input-wrapper" *ngIf="!readOnly" [ngClass]="{ 'input-wrapper-disabled': isLoading || isStreaming }">
    <div class="selected-file-chip" *ngIf="selectedFile">
      <svg-icon [src]="getFileIcon(selectedFile.name)" class="file-type-icon"></svg-icon>
      <span class="file-name">{{ selectedFile.name }}</span>
      <span class="file-size">{{ formatFileSize(selectedFile.size) }}</span>
      <button class="remove-btn" aria-label="Remove attached file" title="Remove attached file" (click)="removeFile()" type="button">
        <svg-icon src="assets/icons/close-icon.svg" aria-hidden="true"></svg-icon>
      </button>
    </div>
    <textarea
      id="existing-chat-message-input"
      aria-label="Message DEO"
      class="input-textarea"
      [ngModel]="currentMessage"
      (ngModelChange)="onMessageChange($event)"
      placeholder="Message DEO..."
      (keypress)="onKeyPress($event)"
      [disabled]="isLoading"
      rows="2"
    ></textarea>
    <div class="button-row" [ngClass]="{ 'button-row-disabled': isLoading || isStreaming }">
      <div class="left-actions">
        <nz-upload *ngIf="isAttachFileEnabled" [nzBeforeUpload]="beforeUpload" [nzShowUploadList]="false" nzAccept=".txt,.pdf,.csv,.md" [nzDisabled]="isStreaming || isLoading">
          <button class="attach-btn" nz-button type="button" [disabled]="isStreaming || isLoading" title="Attach file">
            <svg-icon src="assets/icons/attachment.svg"></svg-icon>
            <span>Attach file</span>
          </button>
        </nz-upload>

        <button
          *ngIf="isLiveTranscriptionEnabled"
          class="dictate-btn"
          [class.listening]="isListening"
          [disabled]="isStreaming || isLoading"
          (click)="onToggleVoiceInput()"
          [title]="isListening ? 'Stop recording' : 'Start voice input'"
          [attr.aria-label]="isListening ? 'Stop recording' : 'Start voice input'"
          type="button"
        >
          <svg-icon src="assets/icons/microphone.svg"></svg-icon>
          <span class="dictate-text">{{ isListening ? 'Listening...' : 'Dictate' }}</span>
        </button>
      </div>
      <!-- Stop button replaces Send only while a response is streaming and the feature flag is on -->
      <button *ngIf="canStopResponse" class="send-btn stop-btn" type="button" (click)="onStopStreaming()" aria-label="Stop response" title="Stop response">
        <svg-icon src="assets/icons/stop.svg" class="send-icon"></svg-icon>
      </button>
      <button *ngIf="!canStopResponse" class="send-btn" type="button" [disabled]="isSendDisabled" (click)="onSendMessage()" aria-label="Send message">
        <svg-icon src="assets/icons/send.svg" class="send-icon"></svg-icon>
      </button>
    </div>
  </div>
  <div class="input-footer">
    <small class="text-muted"> DEO AI Assist is AI and can make mistakes. Please double-check responses. </small>
  </div>
</div>
